import Backbone from "backbone";
import $ from "jquery";
import _ from "underscore";
import Constants from "../helpers/Constants";

const BaseView = Backbone.View.extend({
  // User Agent matching
  ua: {},

  isSmallScreen:
    typeof document !== "undefined"
      ? document.documentElement.clientWidth < 768
      : false,

  initialize: function (options) {
    this.childViews = [];
    this._isRemoved = false;
    this.access = options?.access
      ? options.access
      : typeof Portal !== "undefined"
        ? Portal.Current.access
        : null;
    _.bindAll(this, "handlePopoverShown", "renderPopovers");
  },

  events: {
    "click a.do-cancel": "goToPreviousRoute",
  },

  /**
   * Opens a sharing modal for social media and direct link sharing of astronomical content.
   *
   * This method creates a ShareModal that allows users to share observing lists, saved settings,
   * observations, and other content via Facebook, Twitter, email, or direct links. When sharing,
   * the content's ACL is updated to allow public read access while maintaining user write permissions.
   *
   * @param {Parse.Object} shareModel - The Parse model object to be shared (e.g., ObservingList, SavedSetting, SkyObjectObservation)
   * @param {string} shareTitle - Title for the shared content (appears in social media posts and email subject)
   * @param {string} shareDescription - Description text for the shared content (used in social media descriptions)
   * @param {string} shareLink - Direct URL link to the shareable content (must be publicly accessible)
   * @param {string} shareImage - URL to an image representing the content (used for social media preview images)
   * @param {string} [screenshotDataBase64] - Optional base64-encoded screenshot data (e.g., from Cesium globe view)
   *                                          If provided, it will be saved as a Parse File and used as the preview image
   *
   * @example
   * // Sharing an observing list
   * this.shareModel(
   *   observingListModel,
   *   "My Messier Objects List",
   *   "A curated list of 15 Messier objects perfect for beginners",
   *   "https://portal.example.com/observing-lists/abc123",
   *   "https://portal.example.com/assets/img/messier-preview.jpg"
   * );
   *
   * @example
   * // Sharing with a screenshot
   * this.shareModel(
   *   savedSettingModel,
   *   "Jupiter Observation Setup",
   *   "Telescope settings for observing Jupiter's moons",
   *   "https://portal.example.com/saved-settings/def456",
   *   "https://portal.example.com/assets/img/jupiter-preview.jpg",
   *   cesiumScreenshotBase64
   * );
   *
   * @throws {Error} Throws if ShareModal cannot be instantiated or if required DOM container is missing
   *
   * @see ShareModal - The modal component that handles the actual sharing interface
   * @see Constants.MODAL_CONTAINER_SELECTOR - The DOM selector for where modals are rendered
   *
   * @since 1.0.0
   */
  shareModel: function (
    shareModel,
    shareTitle,
    shareDescription,
    shareLink,
    shareImage,
    screenshotDataBase64,
  ) {
    // handle download request by presenting options in a modal
    const shareModal = new ShareModal({
      model: {
        shareModel: shareModel,
        shareTitle: shareTitle,
        shareDescription: shareDescription,
        shareLink: shareLink,
        shareImage: shareImage,
        screenshotDataBase64: screenshotDataBase64,
      },
    });
    // keep track of child views - they're removed in BaseView.remove()
    this.childViews.push(shareModal);

    // add the modal into the designated modal container
    $(Constants.MODAL_CONTAINER_SELECTOR).html(shareModal.el);

    // show the modal
    shareModal.show();
  },

  triggerFileDownload: function (filename, content, mimetype) {
    var element = document.createElement("a");
    mimetype = mimetype || "text/plain";
    element.setAttribute(
      "href",
      `data:${mimetype};charset=utf-8,${encodeURIComponent(content)}`,
    );
    element.setAttribute("download", filename);
    element.style.display = "none";
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  },

  goToPreviousRoute: function (e) {
    if (e.target) {
      e.preventDefault();
    }

    const hasHash = /#\/[a-zA-Z]+/.test(e);

    if (hasHash) {
      Backbone.history.navigate(e, { trigger: true });
    } else if (window.history.length > 1) {
      window.history.back();
    } else if (e.target) {
      Backbone.history.navigate(e.target.attributes.href.value, {
        trigger: true,
      });
    }
  },

  handlePopoverShown: function (e) {
    const objectId = e.target.dataset.id;

    $(".delete-yes").one("click.portal", () => {
      // Hide and dispose popover FIRST, before re-render removes the element
      const deleteElement = document.getElementById(`delete${objectId}`);
      const popoverInstance =
        window.bootstrap?.Popover?.getInstance(deleteElement);
      if (popoverInstance) {
        popoverInstance.dispose();
      }

      const objectToDelete = _.find(this.collection, (item) => {
        return item.id === objectId;
      });

      if (objectToDelete) {
        objectToDelete.delete().then(() => {
          const index = this.collection.indexOf(objectToDelete);
          if (index > -1) {
            this.collection.splice(index, 1);
          }
          Backbone.trigger("collection:change", this.collection);
        });
      }
    });

    $(".delete-no").one("click.portal", () => {
      const deleteElement = document.getElementById(`delete${objectId}`);
      const popoverInstance =
        window.bootstrap?.Popover?.getInstance(deleteElement);
      if (popoverInstance) {
        popoverInstance.hide();
      }
    });
  },

  renderPopovers: function (title, html) {
    const self = this;
    if (this && this.access) {
      // Use event delegation for better performance with large lists
      // Unbind previous handlers to prevent duplicates
      this.$el.off("click.popover-delete");

      if (this.access.get("hasPremium")) {
        // Premium: create popovers on-demand when clicked
        this.$el.on("click.popover-delete", '[data-bs-toggle="popover"].do-delete', function (e) {
          e.preventDefault();
          e.stopPropagation();

          const btn = this;

          // Check if popover already exists
          let popoverInstance = window.bootstrap?.Popover?.getInstance(btn);

          if (!popoverInstance && window.bootstrap?.Popover) {
            // Create popover on first click
            popoverInstance = new window.bootstrap.Popover(btn, {
              title: `Delete this ${title}?`,
              html: true,
              sanitize: false,
              trigger: "manual",
              content: () => {
                const btnYes = `<button class="btn btn-danger delete-yes" data-id="${btn.dataset.id}"><i class="fa fa-ban"></i> DELETE</button>`;
                const btnNo = `<button class="btn btn-success delete-no" data-id="${btn.dataset.id}"><i class="fa fa-times"></i> DON'T DELETE</button>`;
                return `<div class="d-flex gap-2">${btnYes} ${btnNo}</div>`;
              },
            });

            // Bind shown event
            $(btn).on("shown.bs.popover", (evt) => {
              self.handlePopoverShown(evt);
            });
          }

          // Toggle the popover
          if (popoverInstance) {
            popoverInstance.toggle();
          }
        });
      } else {
        // Non-premium: show premium overlay
        this.$el.on("click.popover-delete", '[data-bs-toggle="popover"].do-delete', (e) => {
          e.preventDefault();
          Backbone.trigger("overlays:premium");
        });
      }
    }
  },

  renderTooltips: function () {
    // Bootstrap 5 tooltip initialization
    $('[data-bs-toggle="tooltip"]').each(function () {
      // Dispose existing tooltip if it exists
      const existingTooltip = window.bootstrap?.Tooltip?.getInstance(this);
      if (existingTooltip) {
        existingTooltip.dispose();
      }

      // Create new tooltip with Bootstrap 5 API
      if (window.bootstrap?.Tooltip) {
        new window.bootstrap.Tooltip(this);
      }
    });
  },

  runAfterMount: function (fn) {
    const schedule =
      typeof window !== "undefined" && window.requestAnimationFrame
        ? window.requestAnimationFrame.bind(window)
        : (cb) => setTimeout(cb, 0);

    const tick = () => {
      if (this._isRemoved) {
        return;
      }

      const hasBody = typeof document !== "undefined" && document.body;
      if (!this.el || !hasBody || !document.body.contains(this.el)) {
        schedule(tick);
        return;
      }

      fn.call(this);
    };

    schedule(tick);
  },

  remove: function () {
    this._isRemoved = true;
    if (this.childViews) {
      this.childViews.forEach((childView) => childView.remove());
    }
    this.$el.empty();
    this.undelegateEvents();
    this.stopListening();
    if (this.formValidator) {
      this.formValidator.destroy();
    }
    if (this.dataTable) {
      this.dataTable.destroy();
    }
    return this;
  },
});

/**
 * Custom extend method that properly merges event objects when extending BaseView.
 *
 * This method extends Backbone's default View.extend functionality to ensure that
 * event handlers are properly merged between parent and child views. Without this
 * custom implementation, child view events would completely override parent events
 * instead of being merged together.
 *
 * @param {Object} child - The child view definition object containing properties and methods
 * @param {Object} [child.events] - Event hash mapping events to handler methods
 * @param {Function} [child.initialize] - Initialization function for the child view
 * @param {Object} [child.*] - Any other Backbone View properties (el, tagName, className, etc.)
 *
 * @returns {Function} A new Backbone View constructor that extends BaseView with merged events
 *
 * @see {@link http://backbonejs.org/#View-extend} Backbone.View.extend documentation
 * @see {@link https://underscorejs.org/#extend} Underscore.js extend for object merging
 *
 * @since 1.0.0
 */
BaseView.extend = function (child) {
  const view = Backbone.View.extend.apply(this, arguments);
  view.prototype.events = _.extend({}, this.prototype.events, child.events);

  return view;
};

export default BaseView;
